/**
 * Authentication Client
 * Handles UI side of SSO delegation flow
 * - Reads code from URL fragment
 * - Cleans URL history
 * - Calls backend to exchange code for session
 */

import type {
  DelegationCode,
  ConsumeCodeRequest,
  ConsumeCodeResponse,
  AuthSession,
  JWTPayload,
  SessionAccess,
  WireSessionScope,
} from '@/types/auth';
import { normalizeSessionScope } from '@/lib/session-scope';
import { withRetry } from '@/lib/retry';

/**
 * Auth error with HTTP status for differentiated error handling (403 vs other)
 */
export class AuthError extends Error {
    constructor(
        message: string,
        public status: number,
    ) {
        super(message);
        this.name = 'AuthError';
    }
}

/**
 * Whether an exchange failure is worth retrying. Transient = no HTTP status
 * (network/fetch error) or a 5xx/408/429 response. A 4xx AuthError (400/401/403 —
 * bad/expired/used code, PKCE failure, forbidden brand access) is terminal: the
 * single-use code is already dead, so retrying is pointless.
 */
function isTransientExchangeError(err: unknown): boolean {
  if (err instanceof AuthError) {
    return err.status >= 500 || err.status === 408 || err.status === 429;
  }
  return true;
}

const STORAGE_KEY_OAUTH_STATE = 'oauth_state';
const STORAGE_KEY_RETURN_TO = 'post_auth_return_to';
const STORAGE_KEY_AUTH_ORIGIN = 'auth_origin_v1';

const API_BASE_URL = import.meta.env.VITE_API_URL;
if (!API_BASE_URL) {
  throw new Error('VITE_API_URL is not set');
}

// Storage keys. Versioned: the cached shape gained `access` and lost `agent_access` (K5), and a
// session cached by the previous build would render the app with no channels — which reads as a
// tenant that sells nothing. Bumping the key makes such a cache a miss, so the first load
// revalidates against GET /sso/session instead of trusting it.
const STORAGE_KEY_SESSION = 'auth_session_v2';

// Auth error event (imported from gupshup-client)
export const AUTH_ERROR_EVENT = 'auth:unauthorized';

/**
 * Parse delegation code from URL fragment
 * Fragment format: #code=xxx&state=yyy&nonce=zzz&code_verifier=zzz (dev mode)
 */
export function parseDelegationCodeFromFragment(): DelegationCode | null {
  if (!window.location.hash) {
    return null;
  }

  const fragment = window.location.hash.substring(1); // Remove '#'
  const params = new URLSearchParams(fragment);

  const code = params.get('code');
  const state = params.get('state');
  const nonce = params.get('nonce');

  if (!code || !state) {
    return null;
  }

  return {
    code,
    state,
    nonce: nonce || undefined,
    code_verifier: params.get('code_verifier') || undefined,
  };
}

/**
 * Clear URL fragment from history (security best practice)
 * Replaces current URL without the fragment
 */
export function clearUrlFragment(): void {
  if (window.history.replaceState) {
    const cleanUrl = window.location.pathname + window.location.search;
    window.history.replaceState({}, document.title, cleanUrl);
  }
}

/**
 * Get PKCE verifier by state
 * Format: pkce:{state}:verifier (matches Main UI storage pattern)
 */
export function getPKCEVerifier(state: string): string | null {
  return localStorage.getItem(`pkce:${state}:verifier`);
}

/**
 * Clear PKCE verifier
 * Format: pkce:{state}:verifier (matches Main UI storage pattern)
 */
export function clearPKCEVerifier(state: string): void {
  localStorage.removeItem(`pkce:${state}:verifier`);
}

/**
 * Exchange delegation code for session via backend
 * The backend will use code_verifier to exchange with AUTH_SERVICE
 */
async function exchangeCodeViaBackend(
  code: string,
  state: string,
  code_verifier: string
): Promise<ConsumeCodeResponse> {
  const response = await fetch(`${API_BASE_URL}/sso/consume`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      code,
      state,
      code_verifier,
    } as ConsumeCodeRequest),
    credentials: 'include', // Important for HttpOnly cookies
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new AuthError(`Token exchange failed (${response.status}): ${errorText}`, response.status);
  }

  return response.json();
}

/**
 * Decode JWT payload without verification (verification happens on server)
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    // Add padding if needed
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    const decoded = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));

    return JSON.parse(decoded);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Check if JWT is expired
 */
export function isJWTExpired(payload: JWTPayload): boolean {
  if (!payload.exp) {
    return true;
  }

  // Add 60 second buffer
  const now = Math.floor(Date.now() / 1000);
  return payload.exp - 60 < now;
}

/**
 * Validate JWT payload
 */
export function validateJWTPayload(payload: JWTPayload, expectedAudience?: string): boolean {
  // Check expiration
  if (isJWTExpired(payload)) {
    console.error('JWT is expired');
    return false;
  }

  // Check not before
  if (payload.nbf) {
    const now = Math.floor(Date.now() / 1000);
    if (now < payload.nbf) {
      console.error('JWT not yet valid (nbf claim)');
      return false;
    }
  }

  // Check audience if provided
  if (expectedAudience) {
    const audiences = Array.isArray(payload.aud) ? payload.aud : [payload.aud];
    if (!audiences.includes(expectedAudience)) {
      console.error('JWT audience mismatch');
      return false;
    }
  }

  // Check required claims
  if (!payload.iss || !payload.sub) {
    console.error('JWT missing required claims (iss, sub)');
    return false;
  }

  return true;
}

/**
 * Store session in sessionStorage
 */
function storeSession(session: AuthSession): void {
  sessionStorage.setItem(STORAGE_KEY_SESSION, JSON.stringify(session));
}

/**
 * Clear session from sessionStorage
 */
export function clearSession(): void {
  sessionStorage.removeItem(STORAGE_KEY_SESSION);
}

/**
 * Handle auth error (401/403) from API
 * Clears session and can trigger re-authentication
 */
export function handleAuthError(): void {
  console.log('Handling auth error: clearing session');
  clearSession();
}

/**
 * Get stored auth session (client-side cache)
 */
export function getStoredSession(): AuthSession | null {
  const stored = sessionStorage.getItem(STORAGE_KEY_SESSION);
  if (!stored) {
    return null;
  }

  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

/**
 * Main SSO consume flow - called when app loads with #code fragment
 * Steps:
 * 1. Parse code from URL fragment
 * 2. Get PKCE verifier from URL (dev mode) or storage (prod mode)
 * 3. Send code + verifier to backend for exchange (retrying transient failures)
 * 4. Backend establishes HttpOnly session cookie and returns user info
 * 5. Store session info in sessionStorage for UI state
 * 6. Clear the fragment + verifier only AFTER the exchange settles
 *
 * The delegation code is single-use, so the URL fragment is NOT cleared until the
 * exchange resolves or terminally fails. Clearing it up front would burn the code
 * on the first transient hiccup, stranding the user (reload has no code left).
 */
export async function consumeSSOCode(): Promise<AuthSession> {
  // 1. Parse code from fragment
  const delegation = parseDelegationCodeFromFragment();
  if (!delegation) {
    throw new Error('No delegation code found in URL fragment');
  }

  console.log('SSO delegation code found, exchanging with backend...');

  // 1b. Verify state if we stored one (CSRF protection)
  // State may be absent when the delegation was initiated from the dashboard sidebar
  // (cross-origin, so no sessionStorage was set). PKCE code_verifier provides equivalent security.
  const storedState = sessionStorage.getItem(STORAGE_KEY_OAUTH_STATE);
  if (storedState && storedState !== delegation.state) {
    console.warn('OAuth state mismatch — clearing stale state. PKCE verifier provides security.');
  }
  sessionStorage.removeItem(STORAGE_KEY_OAUTH_STATE);

  // 2. Get PKCE verifier — prefer URL fragment (cross-subdomain), fall back to localStorage (same-origin)
  const code_verifier = delegation.code_verifier || getPKCEVerifier(delegation.state);

  if (!code_verifier) {
    throw new Error('PKCE verifier not found in URL or storage');
  }

  console.log('PKCE verifier source:', delegation.code_verifier ? 'URL fragment' : 'localStorage');

  try {
    // 3. Exchange code via backend (sets httpOnly cookie and returns session info).
    // Retry transient failures (network / 5xx / 408 / 429); a 4xx is terminal.
    // Budget ~42s of backoff: enough to ride out a backend deploy window while
    // staying under the delegation code's ~60s TTL (an expired code fails 4xx → terminal).
    const response = await withRetry(
      () => exchangeCodeViaBackend(delegation.code, delegation.state, code_verifier),
      { maxAttempts: 8, baseDelayMs: 1500, maxDelayMs: 8000, shouldRetry: isTransientExchangeError }
    );

    console.log('SSO exchange successful, cookie set');

    // 4. Parse expires_at from ISO string to Unix timestamp
    const expiresAt = new Date(response.data.expires_at).getTime() / 1000;

    // 5. Create session object for UI state (includes user info from response)
    const session: AuthSession = {
      authenticated: true,
      expires_at: expiresAt,
      user: response.data.user ? {
        sub: response.data.user.sub,
        email: response.data.user.email,
        name: response.data.user.name,
      } : undefined,
      scope: normalizeSessionScope(response.data.scope),
      access: response.data.access ?? null,
    };

    // Store session in sessionStorage (client-side cache)
    storeSession(session);

    // 6. Success — now clear the fragment (security) and the one-time verifier.
    clearUrlFragment();
    clearPKCEVerifier(delegation.state);

    console.log('Session established for user:', session.user?.sub);

    return session;
  } catch (error) {
    console.error('SSO exchange failed:', error);
    // Terminal failure (retries exhausted or non-retryable): the code is spent, so
    // clear the fragment + verifier. A reload then cleanly re-mints via the dashboard
    // rather than re-attempting a dead code.
    clearUrlFragment();
    clearPKCEVerifier(delegation.state);
    throw error;
  }
}

/**
 * Get current session from sessionStorage cache
 * Note: We rely on sessionStorage because backend doesn't have /sso/me endpoint yet
 * The session is established during SSO code exchange and cached locally
 */
export async function getCurrentSession(): Promise<AuthSession> {
  const stored = getStoredSession();

  // If we have a stored session and it's not expired, return it
  if (stored?.authenticated && stored.expires_at) {
    const now = Math.floor(Date.now() / 1000);
    // Add 60 second buffer
    if (stored.expires_at - 60 > now) {
      return stored;
    }
    // Session expired, clear it
    console.log('Stored session expired');
    clearSession();
    return { authenticated: false };
  }

  // No valid stored session — validate the HttpOnly cookie against the backend
  // (GET /sso/session) and rebuild the client-side cache from it. Throws when
  // the backend is unreachable (callers retry).
  const remote = await fetchSessionStatus();
  if (remote.authenticated) {
    storeSession(remote);
    return remote;
  }
  return { authenticated: false };
}

/**
 * Validates the session cookie and returns the session (incl. its brand access
 * scope) from the backend. GET /sso/session answers 200 with `authenticated: false`
 * when the cookie is missing/invalid, so a network failure or a non-OK response
 * (e.g. 5xx from the proxy during a backend deploy) means the backend is
 * unreachable — that THROWS instead of masquerading as "logged out", so callers
 * can retry rather than redirect to re-auth against a down backend.
 */
export async function fetchSessionStatus(): Promise<AuthSession> {
  const response = await fetch(`${API_BASE_URL}/sso/session`, { credentials: 'include' });
  if (!response.ok) {
    throw new AuthError(`Session status check failed (${response.status})`, response.status);
  }
  const status = (await response.json()) as {
    authenticated: boolean;
    expires_at?: string;
    user?: AuthSession['user'];
    scope?: WireSessionScope | null;
    access?: SessionAccess | null;
  };
  if (!status.authenticated) return { authenticated: false };
  return {
    authenticated: true,
    expires_at: status.expires_at ? new Date(status.expires_at).getTime() / 1000 : undefined,
    user: status.user,
    scope: normalizeSessionScope(status.scope),
    access: status.access ?? null,
  };
}

/**
 * Logout - clear session and call backend to clear cookies
 */
export async function logout(): Promise<void> {
  // Call backend logout endpoint to clear HttpOnly cookies
  try {
    await fetch(`${API_BASE_URL}/sso/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch (error) {
    console.error('Logout request failed:', error);
  }

  clearSession();
}

/**
 * Only allow in-app absolute paths as post-auth destinations ('//' and '/\'
 * are protocol-relative URLs, i.e. open redirects).
 */
export function isSafeReturnToPath(path: string | null | undefined): path is string {
  return !!path && path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/\\');
}

/**
 * Remember the page the user was on before being sent to the dashboard's
 * /oauth/authorize, so we can restore it after the code exchange. Survives the
 * dashboard login detour because the whole flow stays in the same tab.
 */
export function storeReturnToPath(path: string): void {
  if (isSafeReturnToPath(path) && path !== '/') {
    sessionStorage.setItem(STORAGE_KEY_RETURN_TO, path);
  } else {
    sessionStorage.removeItem(STORAGE_KEY_RETURN_TO);
  }
}

/** Read (without consuming) the stored pre-auth path, if it is safe to use. */
export function getStoredReturnToPath(): string | null {
  const path = sessionStorage.getItem(STORAGE_KEY_RETURN_TO);
  return isSafeReturnToPath(path) ? path : null;
}

export function clearStoredReturnToPath(): void {
  sessionStorage.removeItem(STORAGE_KEY_RETURN_TO);
}

/**
 * Which app opened this tab's session: the `from` param carried next to a delegation code.
 * Per-tab, survives reloads and session swaps (clearSession leaves it alone); rewritten only
 * when a new code arrives, so a dashboard-initiated code clears it. Never sent to the backend.
 */
export type AuthOrigin = 'cashier';

export function setAuthOrigin(from: string | null): void {
  try {
    if (from === 'cashier') sessionStorage.setItem(STORAGE_KEY_AUTH_ORIGIN, from);
    else sessionStorage.removeItem(STORAGE_KEY_AUTH_ORIGIN);
  } catch {
    // Storage unavailable: behave as a dashboard session.
  }
}

/**
 * Set right before the app navigates away on logout, so an in-flight 401 landing after the
 * cookie died cannot fire a re-auth redirect (cashier /comercio would silently log back in).
 */
let leaving = false;
export function markLeaving(): void {
  leaving = true;
}
export function isLeaving(): boolean {
  return leaving;
}

export function getAuthOrigin(): AuthOrigin | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY_AUTH_ORIGIN) === 'cashier' ? 'cashier' : null;
  } catch {
    return null;
  }
}

/**
 * Generate and store a random OAuth state for CSRF protection
 */
export function generateOAuthState(): string {
  const state = crypto.randomUUID();
  sessionStorage.setItem(STORAGE_KEY_OAUTH_STATE, state);
  return state;
}

export const authClient = {
  parseDelegationCodeFromFragment,
  clearUrlFragment,
  getPKCEVerifier,
  clearPKCEVerifier,
  consumeSSOCode,
  getCurrentSession,
  fetchSessionStatus,
  logout,
  validateJWTPayload,
  decodeJWT,
  isJWTExpired,
};
